/**
 * The expanded Moonlight card — what the chip is FOR.
 *
 * The chip painted a value and did nothing else. The owner's words: "nothing
 * happens on hover or whatever, so it's really not clear what it is even", and
 * "maybe we need a new screen for what happens if we click this moonlight
 * element". This is that surface.
 *
 * IT IS THE ONLY PLACE A DUEL COUNT APPEARS, and it is hedged, computed for
 * this player's actual tier, and never promised.
 *
 * NO PvP FIGURE. A duel's cost is split across its seats, so what a PvP duel
 * costs YOU depends on who you play with — and the owner's call was that a
 * varying number reads as a promise of a boost we cannot make. The counts here
 * are for a duel you play alone; PvP only ever makes them go further.
 *
 * Putting the story count and the manga count side by side is also the whole
 * manga lesson: it asserts no multiplier and quotes no price, and the reader
 * divides one by the other themselves.
 *
 * It EXPLAINS. It does not sell — the store owns that — and it gates nothing.
 */

import { uiPx } from './ui-scale.js';

const MOONLIGHT_CARD_ID = 'moonlight-card';

let openState = null;

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function closeMoonlightCard() {
  if (!openState) return;
  const { node, anchor, onKey, onDown, describedBy } = openState;
  openState = null;
  document.removeEventListener('keydown', onKey, true);
  document.removeEventListener('mousedown', onDown, true);
  document.removeEventListener('pointerdown', onDown, true);
  window.removeEventListener('blur', onDown);
  document.removeEventListener('visibilitychange', onDown);
  if (anchor && describedBy) anchor.removeAttribute('aria-describedby');
  else if (anchor) anchor.setAttribute('aria-expanded', 'false');
  if (node && node.parentNode) node.parentNode.removeChild(node);
}

/**
 * The card's words, from the opener's translator when it has them.
 *
 * `i18n.t` answers a key it cannot resolve with the key itself — every key,
 * until ui.json has loaded, and for good if it never does — so a hover during
 * a cold load printed "moonlight.name" and "moonlight.storyCount". A
 * translation is used only when it is not the key; otherwise the English,
 * interpolated the same way.
 *
 * @param {Function} [t] the opener's translator, `i18n.t`-shaped
 */
function wordsFrom(t) {
  return (key, fallback, replacements = {}) => {
    let translated = null;
    try { translated = typeof t === 'function' ? t(key, replacements) : null; } catch { /* fall back */ }
    const source = typeof translated === 'string' && translated && translated !== key
      ? translated
      : fallback;
    return Object.entries(replacements)
      .reduce((text, [name, value]) => text.split(`{{${name}}}`).join(String(value)), String(source));
  };
}

function positiveCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

function formatDay(value) {
  const at = Number(value);
  if (!Number.isFinite(at) || at <= 0) return null;
  try {
    return new Date(at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  } catch {
    return null;
  }
}

function formatReturn(status) {
  return formatDay(status?.returnsAt);
}

/* Moonlight bought but still arriving: a new pass or refill releases it in
   four equal weekly parts (withdrawalRelease.js). */
function formatNextRelease(status) {
  return Number(status?.pendingReleaseUsdMicros) > 0 ? formatDay(status?.nextReleaseAt) : null;
}

/* Above one cycle: the surplus a refill or an upgrade carried in, which the
   cycle date does not keep (the chip paints the same fact as its halo). */
function isAboveOneCycle(status) {
  return Number(status?.fraction) >= 1 && Number(status?.haloFraction) > 0;
}

// One date from the authoritative allowance clock. Historical purchases keep
// their promised release/refill date until their weekly transition.
function cadenceLine(status, uiText) {
  if (String(status?.subscriptionType || 'free') === 'free') {
    const date = formatReturn(status);
    return date ? uiText('moonlight.returnsWeekly', 'Some Moonlight returns every week, next on {{date}}', { date }) : null;
  }
  if (status?.weeklyReset === true) {
    const date = formatReturn(status);
    return date ? uiText(
      'moonlight.resetsWeekly',
      'Resets {{date}}',
      { date },
    ) : null;
  }
  const releaseDate = formatNextRelease(status);
  if (releaseDate) {
    return uiText('moonlight.releaseNext', 'More of your Moonlight arrives {{date}}', { date: releaseDate });
  }
  const date = formatReturn(status);
  if (!date) return null;
  return isAboveOneCycle(status)
    ? uiText('moonlight.resets', 'Moonlight resets {{date}}', { date })
    : uiText('moonlight.refills', 'Moonlight refills {{date}}', { date });
}

/**
 * @param {HTMLElement} anchor the chip that summoned it
 * @param {object} status the authoritative Moonlight status
 * @param {{t?: Function, note?: string, noteSaysWhen?: boolean}} [options]
 *   translator, and an optional line the opener adds under the counts (the
 *   store's "+" uses it to say what a refill is); `noteSaysWhen` when that
 *   line already says when the next part arrives, so the card does not say
 *   it twice
 */
function openMoonlightCard(anchor, status, { t, note, noteSaysWhen = false } = {}) {
  if (!anchor) return null;
  closeMoonlightCard();

  const uiText = wordsFrom(t);

  const node = el('div', 'moonlight-card');
  node.id = MOONLIGHT_CARD_ID;
  node.setAttribute('role', 'dialog');
  node.setAttribute('aria-label', uiText('moonlight.name', 'Moonlight'));

  const known = status && status.state === 'known';

  const head = el('div', 'moonlight-card__head');
  head.appendChild(el('span', 'moonlight-card__percent',
    known && Number.isFinite(Number(status.percent)) ? `${Math.max(0, Math.round(status.percent))}%` : '—'));
  head.appendChild(el('span', 'moonlight-card__name', uiText('moonlight.name', 'Moonlight')));
  node.appendChild(head);

  const rows = el('div', 'moonlight-card__rows');

  /* A count is OMITTED when it is unknown — never printed as zero, which would
     read as "you cannot play" to someone whose read merely failed. */

  /* THE SAME MOONLIGHT WITH ADVANCED AI OFF. One count answers "how many duels
     do I have left?" only for the mode it was priced in, and the switch is per
     duel — so the card names both. Absent for free, which has no Pro Storyteller to
     turn off; the server sends null rather than repeating the story figure. */
  /* ORDER: basic, Pro Storyteller, manga — cheapest mode first, so the numbers
     read as a ladder the player climbs rather than three unrelated figures.
     The owner asked for exactly this sequence. */
  const basic = known ? positiveCount(status.basicDuels) : null;
  if (basic !== null) {
    rows.appendChild(el('p', 'moonlight-card__row',
      uiText('moonlight.basicCount', 'Basic Storyteller: around {{n}} duels', { n: basic })));
  }

  const story = known ? positiveCount(status.storyDuels) : null;
  if (story !== null) {
    rows.appendChild(el('p', 'moonlight-card__row',
      uiText('moonlight.storyCount', 'Pro Storyteller: around {{n}} duels', { n: story })));
  }

  /* The manga row belongs to the tier that can actually draw panels. Showing
     it to anyone else advertises something they do not have. */
  const manga = known && status.subscriptionType === 'moonfall_manga'
    ? positiveCount(status.mangaDuels)
    : null;
  if (manga !== null) {
    rows.appendChild(el('p', 'moonlight-card__row',
      uiText('moonlight.mangaCount', 'Manga + Pro Storyteller: around {{n}} duels', { n: manga })));
  }

  if (rows.childNodes.length) node.appendChild(rows);


  const cadence = known && !noteSaysWhen ? cadenceLine(status, uiText) : null;
  if (cadence) node.appendChild(el('p', 'moonlight-card__returns', cadence));

  /* REWARD MOONLIGHT OUTLIVES THE DATE ABOVE (owner, 2026-10-05): an
     invitation's or a code's Moonlight is kept until it is spent, so a pass
     holder's moon stays above 100% after its weekly reset. One line, and only
     while some is held. */
  const rewardPercent = known ? positiveCount(status.rewardPercent) : null;
  if (rewardPercent !== null) {
    node.appendChild(el('p', 'moonlight-card__reward',
      uiText('moonlight.rewardKept', '{{percent}}% is reward Moonlight, kept until you use it', { percent: rewardPercent })));
  }

  if (typeof note === 'string' && note.trim()) {
    node.appendChild(el('p', 'moonlight-card__note', note.trim()));
  }

  document.body.appendChild(node);
  anchor.setAttribute('aria-expanded', 'true');

  /* ANY pointer press elsewhere dismisses it. Clicking a chip opens the
     store, which covers the lobby — and a covered element never receives
     `mouseleave`, so the tip stayed on screen and was still there when the
     player came back. The owner's report, and their rule: "this clicking to
     keep these always on should not be a thing". A press on the chip itself
     is left to the chip's click, which closes the card before the store opens
     (main.js); closing it at touch-down made a phone's tap blink the card out
     until the lift opened it again. Blur and visibilitychange carry no
     pointer target and always close. */
  const onKey = (event) => { if (event.key === 'Escape') closeMoonlightCard(); };
  const onDown = (event) => {
    const target = event?.target;
    if ((event?.type === 'mousedown' || event?.type === 'pointerdown')
      && target instanceof Node && anchor.contains(target)) return;
    closeMoonlightCard();
  };
  document.addEventListener('keydown', onKey, true);
  document.addEventListener('mousedown', onDown, true);
  document.addEventListener('pointerdown', onDown, true);
  // A tab-away or a surface change is the same situation: the pointer will
  // never come back to fire `mouseleave`.
  window.addEventListener('blur', onDown);
  document.addEventListener('visibilitychange', onDown);
  openState = { node, anchor, onKey, onDown };

  // Anchored to the chip, clamped into the viewport. Read after insertion so
  // the measurement is of the real box rather than an assumed one.
  const a = anchor.getBoundingClientRect();
  const b = node.getBoundingClientRect();
  const edge = uiPx(8);
  const left = Math.max(edge, Math.min(a.left, window.innerWidth - b.width - edge));
  node.style.left = `${Math.round(left)}px`;
  node.style.top = `${Math.round(a.bottom + edge)}px`;

  return node;
}

/**
 * The Stardust chip's hover, deliberately much lighter.
 *
 * The owner's line: "hovering stardust should just say stardust, in a similar
 * style to what we show in moonlight, but less bloated". Stardust needs no
 * explaining — the number is already the whole fact — so this is the same
 * material and position with one word in it. Anything more would be a second
 * Moonlight card attached to something that does not need one.
 *
 * @param {HTMLElement} anchor the Stardust chip
 * @param {{t?: Function}} [options] translator
 */
function openStardustTip(anchor, { t } = {}) {
  if (!anchor) return null;
  closeMoonlightCard();
  const uiText = wordsFrom(t);

  const node = el('div', 'moonlight-card moonlight-card--tip');
  node.id = MOONLIGHT_CARD_ID;
  node.setAttribute('role', 'tooltip');
  node.appendChild(el('span', 'moonlight-card__name', uiText('common.stardust', 'Stardust')));

  document.body.appendChild(node);
  anchor.setAttribute('aria-describedby', MOONLIGHT_CARD_ID);

  const onKey = (event) => { if (event.key === 'Escape') closeMoonlightCard(); };
  const onDown = () => closeMoonlightCard();
  document.addEventListener('keydown', onKey, true);
  document.addEventListener('mousedown', onDown, true);
  document.addEventListener('pointerdown', onDown, true);
  window.addEventListener('blur', onDown);
  document.addEventListener('visibilitychange', onDown);
  openState = { node, anchor, onKey, onDown, describedBy: true };

  const a = anchor.getBoundingClientRect();
  const b = node.getBoundingClientRect();
  const edge = uiPx(8);
  node.style.left = `${Math.round(Math.max(edge, Math.min(a.left, window.innerWidth - b.width - edge)))}px`;
  node.style.top = `${Math.round(a.bottom + edge)}px`;
  return node;
}

function isMoonlightCardOpen() {
  return Boolean(openState);
}

/**
 * A focus listener that opens a card only when the KEYBOARD put focus there.
 *
 * The store hands focus back to the chip that opened it when it closes, and a
 * card opened on that focus sat over the lobby until the player clicked it away
 * (owner, 2026-09-25: "when you go back to the lobby, it's also open there and
 * you also have to click away"). A mouse player gets the card from hover; a
 * keyboard player still gets it from Tab. `:focus-visible` is the browser's own
 * answer to "did the keyboard put focus here"; an engine that cannot answer
 * keeps the keyboard path.
 *
 * @param {Element} anchor the control whose focus this listens to
 * @param {() => void} open what opens the card
 */
function onKeyboardFocus(anchor, open) {
  return () => {
    let keyboard = true;
    try { keyboard = anchor.matches(':focus-visible'); } catch { /* no :focus-visible: keep it */ }
    if (keyboard) open();
  };
}

export {
  openMoonlightCard, openStardustTip, closeMoonlightCard, isMoonlightCardOpen, onKeyboardFocus,
  MOONLIGHT_CARD_ID,
};
